import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import ClassSelector from '../components/ClassSelector';
import ChallengeStartPanel from '../components/ChallengeStartPanel';
import CompetitionModeToggle from '../components/CompetitionModeToggle';
import LeaderboardTable from '../components/LeaderboardTable';
import MenuShell from '../components/MenuShell';
import { useAuth } from '../features/auth/AuthContext';
import type { Chapter } from '../features/instructor/instructorApi';
import { useInstructorCourseId } from '../features/instructor/useInstructorCourseId';
import type { CompetitionState } from '../features/leaderboard/leaderboardApi';
import { useCompetitionMutations, useCompetitionQueries } from '../features/leaderboard/useCompetitionData';
import './InstructorLeaderboardPage.css';

function InstructorClassSelection({ courseId }: { courseId: string }) {
  const navigate = useNavigate();
  const { courses, selectCourse } = useInstructorCourseId();
  const courseTitle = courses.find((course) => course.id === courseId)?.title;

  function handleSelectCourse(selectedCourseId: string) {
    selectCourse(selectedCourseId);
    navigate(`/courses/${selectedCourseId}/leaderboard`);
  }

  if (!courseTitle) {
    return null;
  }

  return (
    <>
      <p>{courseTitle}</p>
      {courses.length > 1 && (
        <ClassSelector
          courses={courses}
          selectedCourseId={courseId}
          onChange={handleSelectCourse}
        />
      )}
    </>
  );
}

interface CompetitionControlsProps {
  isInstructor: boolean;
  courseId: string;
  state: CompetitionState | undefined;
  chapters: Chapter[];
  busy: boolean;
  onActivate: (chapterId: string) => void;
  onDeactivate: () => void;
  onReset: () => void;
}

function CompetitionControls({
  isInstructor,
  courseId,
  state,
  chapters,
  busy,
  onActivate,
  onDeactivate,
  onReset,
}: CompetitionControlsProps) {
  if (isInstructor) {
    return (
      <CompetitionModeToggle
        state={state}
        chapters={chapters}
        busy={busy}
        onActivate={onActivate}
        onDeactivate={onDeactivate}
        onReset={onReset}
      />
    );
  }
  return <ChallengeStartPanel courseId={courseId} state={state} />;
}

/**
 * Real-time leaderboard page for a course's competition mode (US7/US1). Reachable by both the
 * owning instructor (with activation controls) and any enrolled student (read-only standings).
 */
function InstructorLeaderboardPage() {
  const { t } = useTranslation();
  const { courseId = '' } = useParams();
  const { role } = useAuth();
  const isInstructor = role === 'INSTRUCTOR';

  const { chaptersQuery, stateQuery, leaderboardQuery } = useCompetitionQueries(courseId);
  const { activateMutation, deactivateMutation, resetMutation } = useCompetitionMutations(courseId);

  const chapterOptions = chaptersQuery.data ?? [];
  const isDisconnected = leaderboardQuery.isError;
  const lastUpdatedAt = leaderboardQuery.dataUpdatedAt;

  return (
    <>
      <MenuShell courseId={courseId} />
      <main className="leaderboard-page">
        <header className="leaderboard-page__hero">
          {isInstructor ? (
            <Link to="/instructor">{t('leaderboard.backToDashboard')}</Link>
          ) : (
            <Link to={`/courses/${courseId}`}>{t('leaderboard.backToCourse')}</Link>
          )}
          <h1>{t('leaderboard.title')}</h1>
          {isInstructor && <InstructorClassSelection courseId={courseId} />}
        </header>

        <CompetitionControls
          isInstructor={isInstructor}
          courseId={courseId}
          state={stateQuery.data}
          chapters={chapterOptions}
          busy={activateMutation.isPending || deactivateMutation.isPending || resetMutation.isPending}
          onActivate={(chapterId) => activateMutation.mutate(chapterId)}
          onDeactivate={() => deactivateMutation.mutate()}
          onReset={() => resetMutation.mutate()}
        />

        {isDisconnected && (
          <p role="alert" className="leaderboard-page__disconnected">
            {t('leaderboard.disconnected')}
          </p>
        )}

        {lastUpdatedAt > 0 && (
          <p className="leaderboard-page__updated">
            {t('leaderboard.lastUpdated', { time: new Date(lastUpdatedAt).toLocaleTimeString() })}
          </p>
        )}

        <LeaderboardTable entries={leaderboardQuery.data ?? []} />
      </main>
    </>
  );
}

export default InstructorLeaderboardPage;
